<template>
  <div class="faqPage">
    <!-- 面包屑导航 -->
    <div class="breadcrumbBar">
      <div class="breadcrumbContent">
        <span class="breadcrumbLink" @click="goBack">{{ $t('menu.service') }}</span>
        <span class="breadcrumbSeparator">></span>
        <span class="breadcrumbLink" @click="goBack">{{ $t('Service.faqTitle') }}</span>
        <template v-if="currentBreadcrumb">
          <span class="breadcrumbSeparator">></span>
          <span class="breadcrumbCurrent">{{ currentBreadcrumb }}</span>
        </template>
      </div>
    </div>

    <div class="faqContainer">
      <!-- 搜索框 -->
      <div class="searchBox">
        <el-input
          v-model="searchKeyword"
          placeholder="请输入您想了解的问题"
          prefix-icon="el-icon-search"
          clearable
          @input="handleSearch"
        ></el-input>
      </div>

      <div class="mainContent">
        <!-- 左侧分类导航 -->
        <div class="categoryNav">
          <el-tree
            ref="categoryTree"
            :data="categoryTreeData"
            :props="treeProps"
            node-key="id"
            :default-expanded-keys="defaultExpandedKeys"
            :current-node-key="currentNodeKey"
            :highlight-current="true"
            :expand-on-click-node="false"
            @node-click="handleNodeClick"
          >
            <template #default="{ node, data }">
              <div class="custom-tree-node">
                <div class="node-label">{{ language === 'en' && data.fullNameEn ? data.fullNameEn : data.fullName }}</div>
                <i
                  v-if="data.children && data.children.length"
                  class="el-icon-arrow-down node-arrow"
                  :class="{ 'is-expanded': node.expanded }"
                ></i>
              </div>
            </template>
          </el-tree>
        </div>

        <!-- 右侧问题列表 -->
        <div class="faqListWrapper">
          <div class="faqList" v-loading="loading">
            <!--添加v-if 点击后展开问题详情            -->
            <div
                class="faqItem"
                v-for="item in allFaqList"
                :key="item.id"
            >
              <div class="faqQuestion" @click="toggleFaq(item)">
                <span>{{ item.title }}</span>
                <i
                    class="el-icon-arrow-down faqArrow"
                    :class="{ expanded: expandedFaqId === item.id }"
                ></i>
              </div>

              <div
                  v-if="expandedFaqId === item.id"
                  class="faqAnswerPreview"
                  v-html="item.content"
              ></div>
            </div>
          </div>

          <!-- 空状态 -->
          <div class="emptyState" v-if="allFaqList.length === 0 && !loading">
            <i class="el-icon-document"></i>
            <p>暂无相关问题</p>
          </div>

          <!-- 分页 -->
          <div class="paginationWrapper">
            <el-pagination
              background
              layout="slot, prev, next, jumper"
              :current-page.sync="currentPage"
              :page-size="pageSize"
              :total="total"
              @current-change="handlePageChange"
            >
              <span class="pageInfo"
                >{{ currentPage }} / {{ totalPages || 1 }}</span
              >
            </el-pagination>
          </div>
        </div>
      </div>
    </div>

    <el-dialog
      :visible.sync="faqDialogVisible"
      :title="selectedFaq.title"
      width="760px"
      custom-class="faqDetailDialog"
      append-to-body
    >
      <div class="dialogFaqContent" v-html="selectedFaq.content"></div>
    </el-dialog>
  </div>
</template>

<script>
import { supportQuestion, supportQuestionInfo, supportQuestionTree } from "@/assets/API/service";
import define from "@/utils/define";
const baceUrl = define.APIURl;
export default {
  name: "FaqPage",
  props: {
    targetId: { type: [String, Number], default: "" },
  },
  data() {
    return {
      language: this.$i18n.locale,
      searchKeyword: "",
      loading: false,

      // 分页
      currentPage: 1,
      pageSize: 10,
      total: 0,

      // 问题数据
      allFaqList: [],

      // 树形分类数据
      categoryTreeData: [],
      treeProps: {
        children: "children",
        label: "fullName",
      },
      defaultExpandedKeys: [],
      currentNodeKey: null,
      currentCategory: null,
      currentBreadcrumb: "",

      // 当前展开的 FAQ id。为空表示全部折叠
      expandedFaqId: "",

      faqDialogVisible: false,
      selectedFaq: {
        title: "",
        content: "",
      },
    };
  },
  computed: {
    totalPages() {
      return Math.ceil(this.total / this.pageSize);
    },
  },
  mounted() {
    this.fetchCategoryTree();
    if (this.targetId) this.fetchTargetFaq();
    else this.fetchFaqList();
  },
  methods: {
    async fetchTargetFaq() {
      this.loading = true;
      try {
        const res = await supportQuestionInfo(this.targetId);
        if (res.code === 200 && res.data) {
          const item = {
            id: res.data.id,
            title: this.language === "zh" ? res.data.title : (res.data.titleEn || res.data.title),
            content: this.language === "zh" ? res.data.content : (res.data.contentEn || res.data.content),
          };
          this.allFaqList = [item];
          this.total = 1;
          this.expandedFaqId = item.id;
          this.openFaqDetailDialog(item);
        }
      } finally {
        this.loading = false;
      }
    },
    // 获取分类树数据
    fetchCategoryTree() {
      supportQuestionTree().then((res) => {
        if (res && res.data) {
          // 处理树形数据，确保每一层都有正确的字段
          this.categoryTreeData = this.processTreeData(res.data.list || []);
          // 默认展开所有层级
          this.defaultExpandedKeys = this.getAllNodeIds(this.categoryTreeData);
          // 默认选中全部
          this.$nextTick(() => {
            this.$refs.categoryTree?.setCurrentKey(null);
          });
        }
        this.loading = false;
      });
    },

    // 处理树形数据，确保字段正确
    processTreeData(data) {
      return data.map((item) => {
        const node = {
          ...item,
          id: item.id,
          fullName: item.fullName || item.name || item.label, // 兼容多种字段名
          fullNameEn: item.fullNameEn,
        };
        if (item.children && item.children.length > 0) {
          node.children = this.processTreeData(item.children);
        }
        return node;
      });
    },

    // 获取所有节点的 id，用于默认展开
    getAllNodeIds(tree) {
      let ids = [];
      tree.forEach((node) => {
        ids.push(node.id);
        if (node.children && node.children.length > 0) {
          ids = ids.concat(this.getAllNodeIds(node.children));
        }
      });
      return ids;
    },

    // 获取问题列表
    fetchFaqList() {
      this.loading = true;
      this.expandedFaqId = "";
      supportQuestion({
        currentPage: this.currentPage,
        pageSize: this.pageSize,
        typeId: this.currentNodeKey || null,
        keyword: this.searchKeyword || null,
      }).then((res) => {
        if (res.code == 200 && res.data.records) {
          this.loading = false;
          this.total = res.data.total || 0;
          this.allFaqList = res.data.records.map((item) => ({
            id: item.id,
            title: this.language == "zh" ? item.title : item.titleEn,
            content: this.language == "zh" ? item.content : item.contentEn,
          }));
        }
      });
    },
    // 树节点点击
    handleNodeClick(data, node) {
      // 判断是否为父节点（有子节点）
      if (data.children && data.children.length > 0) {
        // 父节点：只切换展开/关闭状态，不查询问题列表
        node.expanded = !node.expanded;

        // 关键：清除之前的选中状态，不让父节点被选中
        this.$nextTick(() => {
          // 保持之前选中的叶子节点状态
          if (this.currentNodeKey) {
            this.$refs.categoryTree?.setCurrentKey(this.currentNodeKey);
          } else {
            // 如果之前没有选中任何节点，清除选中
            this.$refs.categoryTree?.setCurrentKey(null);
          }
        });

        return;
      }

      // 叶子节点：只选中这个节点，查询问题列表
      this.currentNodeKey = data.id;
      this.currentCategory = data;
      this.currentBreadcrumb = data.fullName || data.label;
      this.currentPage = 1;

      // 确保只有这个节点被选中
      this.$nextTick(() => {
        this.$refs.categoryTree?.setCurrentKey(data.id);
      });

      this.fetchFaqList();
    },

    // 获取节点及其所有子孙节点的 id 列表
    getAllChildIds(node) {
      let ids = [node.id];
      if (node.children && node.children.length > 0) {
        node.children.forEach((child) => {
          ids = ids.concat(this.getAllChildIds(child));
        });
      }
      return ids;
    },

    // 在树中查找节点
    findNodeById(tree, id) {
      for (const node of tree) {
        if (node.id === id) {
          return node;
        }
        if (node.children && node.children.length > 0) {
          const found = this.findNodeById(node.children, id);
          if (found) return found;
        }
      }
      return null;
    },

    // 搜索
    handleSearch() {
      this.currentPage = 1;
      this.fetchFaqList();
    },

    // 分页
    handlePageChange(page) {
      this.currentPage = page;
      this.fetchFaqList();
    },

    toggleFaq(item) {
      this.expandedFaqId = this.expandedFaqId === item.id ? "" : item.id;
    },
    // 返回
    goBack() {
      this.$emit("back");
    },
    openFaqDetailDialog(item) {
      this.selectedFaq = {
        title: item.title || "",
        content: item.content || "",
      };
      this.faqDialogVisible = true;
    },
  },
};
</script>

<style scoped lang="scss">
.faqPage {
  background: #f7f8fb;
  min-height: 100vh;
}

/* 面包屑导航 */
.breadcrumbBar {
  background: #333;
  padding: 16px 0;

  .breadcrumbContent {
    width: 1200px;
    margin: 0 auto;
    font-size: 14px;
    color: #fff;

    .breadcrumbLink {
      color: #aaa;
      cursor: pointer;

      &:hover {
        color: #fff;
      }
    }

    .breadcrumbSeparator {
      margin: 0 8px;
      color: #666;
    }

    .breadcrumbCurrent {
      color: #fff;
    }
  }
}

/* 主容器 */
.faqContainer {
  width: 1200px;
  margin: 0 auto;
  padding: 40px 0;
}

/* 搜索框 */
.searchBox {
  margin-bottom: 30px;

  ::v-deep .el-input {
    width: 300px;

    .el-input__inner {
      border-radius: 4px;
      border-color: #ddd;

      &:focus {
        border-color: #2b85e5;
      }
    }
  }
}

/* 主内容区 */
.mainContent {
  display: flex;
  gap: 60px;
}

/* 左侧分类导航 */
.categoryNav {
  width: 220px;
  flex-shrink: 0;

  ::v-deep .el-tree {
    background: transparent;

    .el-tree-node {
      &:focus > .el-tree-node__content {
        background-color: transparent;
      }
    }

    .el-tree-node__content {
      height: 40px;
      padding-left: 0 !important;
      position: relative;

      &:hover {
        background-color: transparent;

        .node-label {
          color: #2b85e5;
        }
      }
    }

    // 展开箭头
    .el-tree-node__expand-icon {
      display: none;
    }

    // ========== 重点：节点内容样式 ==========
    .custom-tree-node {
      display: flex;
      align-items: center;
      justify-content: space-between;
      width: 100%;
      padding-right: 8px;
      position: relative;

      .node-label {
        font-size: 14px;
        color: #333;
        transition: color 0.3s;
      }

      .node-arrow {
        font-size: 12px;
        color: #999;
        transition: transform 0.3s;
      }
    }

    // 选中状态与视频教程保持一致
    .el-tree-node.is-current > .el-tree-node__content {
      background-color: transparent;

      .node-label {
        color: #2b85e5;
        font-weight: 500;
      }
    }

    // 第一层节点样式
    & > .el-tree-node > .el-tree-node__content {
      .node-label {
        font-size: 16px;
        font-weight: 600;
      }
    }

    // 展开时箭头旋转
    .el-tree-node.is-expanded > .el-tree-node__content {
      .node-arrow {
        transform: rotate(180deg);
      }
    }

    // 子节点缩进（重要：确保每一层都能显示竖线）
    .el-tree-node__children {
      .el-tree-node__content {
        padding-left: 20px !important;
        position: relative;
        background-color: transparent;
      }

      .el-tree-node__children {
        .el-tree-node__content {
          padding-left: 20px !important;
          position: relative;
          background-color: transparent;
        }

        .el-tree-node__children {
          .el-tree-node__content {
            padding-left: 20px !important;
            position: relative;
            background-color: transparent;
          }
        }
      }
    }
  }
}

/* 右侧问题列表 */
.faqListWrapper {
  flex: 1;
  position: relative;
  min-height: 400px;
}

.faqList {
  min-height: 400px;
}

.faqItem {
  padding: 18px 0;
  border-bottom: 1px solid #e8ebf0;
  cursor: pointer;

  &:first-child {
    padding-top: 0;
  }

  .faqQuestion {
    display: flex;
    align-items: center;
    justify-content: space-between;
    font-size: 16px;
    font-weight: 600;
    color: #333;
    margin-bottom: 10px;
    line-height: 1.5;
    transition: color 0.3s;

    &:hover {
      color: #2b85e5;
    }

    .faqArrow {
      font-size: 14px;
      color: #999;
      transition: transform 0.3s;
    }

    .faqArrow.expanded {
      transform: rotate(180deg);
    }
  }

  .faqAnswerPreview {
    font-size: 14px;
    color: #666;
    line-height: 1.9;
    word-break: break-word;

    ::v-deep img {
      max-width: 100%;
      height: auto;
      object-fit: contain;
    }

    p {
      margin: 0;
    }
  }
}

/* 空状态 */
.emptyState {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 100%;
  color: #999;

  i {
    font-size: 64px;
    margin-bottom: 16px;
  }

  p {
    font-size: 14px;
  }
}

/* 分页 */
.paginationWrapper {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  margin-top: 40px;

  .pageInfo {
    font-size: 14px;
    color: #2b85e5;
    margin-right: 8px;
  }

  ::v-deep .el-pagination {
    padding: 0;

    .btn-prev,
    .btn-next {
      background: #fff;
      border: 1px solid #dcdfe6;
      border-radius: 4px;
      min-width: 28px;
      height: 28px;

      &:hover:not(:disabled) {
        color: #2b85e5;
        border-color: #2b85e5;
      }

      &:disabled {
        color: #c0c4cc;
        cursor: not-allowed;
      }
    }

    .el-pagination__jump {
      margin-left: 12px;
      font-size: 14px;
      color: #606266;

      .el-input {
        width: 50px;
        margin: 0 6px;

        .el-input__inner {
          height: 28px;
          line-height: 28px;
          text-align: center;
          border-color: #dcdfe6;

          &:focus {
            border-color: #2b85e5;
          }
        }
      }
    }
  }
}

::v-deep .faqDetailDialog {
  .el-dialog__body {
    max-height: 60vh;
    overflow-y: auto;
  }

  .dialogFaqContent {
    font-size: 14px;
    color: #666;
    line-height: 1.9;
    word-break: break-word;

    ::v-deep img {
      max-width: 100%;
      height: auto;
      object-fit: contain;
    }

    p {
      margin: 0;
    }
  }
}
</style>
